import {useCallback, useEffect, useMemo, useState} from "react";
import {useProperties} from "../../../../contexts/properties/PropertiesContext";
import {getSessionStorageObject, setSessionStorageObject} from "../../../../libs/localstorage-helper";
import {SortState} from "../logic/sortLogic";
import {emptyThresholds, METRIC_KEYS, MetricKey, MetricThreshold, MonthSelection, Thresholds} from "../logic/types";

const THRESHOLDS_STORAGE_KEY = "dataHealth.thresholds.v1";
const DATES_STORAGE_KEY = "dataHealth.dates.v1";

type SavedDates = {baseMonth: MonthSelection; comparisonMonth: MonthSelection};

function loadThresholdsFromSession(): Thresholds {
    try {
        const parsed = getSessionStorageObject(THRESHOLDS_STORAGE_KEY, {}) as Partial<Thresholds>;
        const result = emptyThresholds();
        for (const metric of METRIC_KEYS) {
            const t = parsed[metric] as MetricThreshold | undefined;
            if (t) {
                result[metric] = {
                    increase: typeof t.increase === "number" ? t.increase : null,
                    decrease: typeof t.decrease === "number" ? t.decrease : null,
                };
            }
        }
        return result;
    } catch {
        return emptyThresholds();
    }
}

function saveThresholdsToSession(thresholds: Thresholds): void {
    setSessionStorageObject(THRESHOLDS_STORAGE_KEY, thresholds);
}

function loadSavedDates(): SavedDates | null {
    try {
        const raw = getSessionStorageObject(DATES_STORAGE_KEY, {}) as Partial<SavedDates>;
        const baseMonth = raw.baseMonth;
        const comparisonMonth = raw.comparisonMonth;
        if (!baseMonth || !comparisonMonth
            || typeof baseMonth.year !== "number" || typeof baseMonth.monthIndex !== "number"
            || typeof comparisonMonth.year !== "number" || typeof comparisonMonth.monthIndex !== "number") {
            return null;
        }
        return {
            baseMonth,
            comparisonMonth
        };
    } catch {
        return null;
    }
}

function saveDates(baseMonth: MonthSelection, comparisonMonth: MonthSelection): void {
    setSessionStorageObject(DATES_STORAGE_KEY, {baseMonth, comparisonMonth});
}

export type StatusFilter = "all" | "outliers" | "noChange";

export type UseDataHealthReportStateAPI = {
    availableProperties: {value: string; label: string}[];
    selectedPropertyIds: string[];
    setSelectedPropertyIds: (ids: string[]) => void;

    baseMonth: MonthSelection;
    setBaseMonth: (m: MonthSelection) => void;
    comparisonMonth: MonthSelection;
    setComparisonMonth: (m: MonthSelection) => void;

    allowedBaseMonths: MonthSelection[];
    allowedComparisonMonths: MonthSelection[];

    thresholds: Thresholds;
    setThreshold: (metric: MetricKey, kind: "increase" | "decrease", value: number | null) => void;

    search: string;
    setSearch: (s: string) => void;

    statusFilter: StatusFilter;
    setStatusFilter: (s: StatusFilter) => void;

    sortState: SortState;
    setSortState: (s: SortState) => void;

    earliestReforecastMonth: MonthSelection | null;
};

function buildMonthRange(from: MonthSelection, to: MonthSelection): MonthSelection[] {
    const result: MonthSelection[] = [];
    let y = from.year;
    let m = from.monthIndex;
    while (y < to.year || (y === to.year && m <= to.monthIndex)) {
        result.push({year: y, monthIndex: m});
        m += 1;
        if (m > 11) {
            m = 0;
            y += 1;
        }
    }
    return result;
}

function nextMonth(m: MonthSelection): MonthSelection {
    return {
        year: m.monthIndex === 11 ? m.year + 1 : m.year,
        monthIndex: m.monthIndex === 11 ? 0 : m.monthIndex + 1,
    };
}

function previousMonth(m: MonthSelection): MonthSelection {
    return {
        year: m.monthIndex === 0 ? m.year - 1 : m.year,
        monthIndex: m.monthIndex === 0 ? 11 : m.monthIndex - 1,
    };
}

function nextMonthOrSame(m: MonthSelection, cap: MonthSelection): MonthSelection {
    const next = nextMonth(m);
    if (next.year > cap.year || (next.year === cap.year && next.monthIndex > cap.monthIndex)) {
        return m;
    }
    return next;
}

function monthCompare(a: MonthSelection, b: MonthSelection): number {
    if (a.year !== b.year) {
        return a.year - b.year;
    }
    return a.monthIndex - b.monthIndex;
}

function isInRange(m: MonthSelection, from: MonthSelection, to: MonthSelection): boolean {
    return monthCompare(m, from) >= 0 && monthCompare(m, to) <= 0;
}

function resolveInitialMonths(
    savedDates: SavedDates | null,
    earliestReforecastMonth: MonthSelection | null,
    now: MonthSelection
): {initialBase: MonthSelection; initialComparison: MonthSelection} {
    const effectiveEarliestBase = (earliestReforecastMonth && monthCompare(earliestReforecastMonth, now) >= 0)
        ? previousMonth(now)
        : earliestReforecastMonth;

    let initialBase = savedDates?.baseMonth ?? (effectiveEarliestBase ?? previousMonth(now));
    const initialComparison = savedDates?.comparisonMonth ?? nextMonthOrSame(initialBase, now);

    if (monthCompare(initialBase, initialComparison) >= 0) {
        initialBase = previousMonth(initialComparison);
    }

    return {initialBase, initialComparison};
}

export function useDataHealthReportState(): UseDataHealthReportStateAPI {
    const {properties} = useProperties();

    const availableProperties = useMemo(
        () => (properties ?? []).map(p => ({value: p.id, label: p.name})),
        [properties]
    );

    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);

    const earliestReforecastMonth: MonthSelection | null = useMemo(() => {
        const source = properties ?? [];
        const relevant = selectedPropertyIds.length > 0
            ? source.filter(p => selectedPropertyIds.includes(p.id))
            : source;
        let min: MonthSelection | null = null;
        for (const p of relevant) {
            if (!min || p.reforecastYear < min.year || (p.reforecastYear === min.year && p.reforecastStartMonthIndex < min.monthIndex)) {
                min = {year: p.reforecastYear, monthIndex: p.reforecastStartMonthIndex};
            }
        }
        return min;
    }, [properties, selectedPropertyIds]);

    const now = useMemo(() => {
        const d = new Date();
        return {year: d.getFullYear(), monthIndex: d.getMonth()};
    }, []);

    const effectiveEarliestBase = useMemo(() => {
        if (!earliestReforecastMonth) {
            return null;
        }
        return monthCompare(earliestReforecastMonth, now) >= 0
            ? previousMonth(now)
            : earliestReforecastMonth;
    }, [earliestReforecastMonth, now]);

    const savedDates = useMemo(() => loadSavedDates(), []);
    const {initialBase: resolvedBase, initialComparison: resolvedComparison} = useMemo(
        () => resolveInitialMonths(savedDates, earliestReforecastMonth, now),
        [savedDates, earliestReforecastMonth, now]
    );

    const [comparisonMonth, setComparisonMonth] = useState<MonthSelection>(resolvedComparison);
    const [baseMonth, setBaseMonthState] = useState<MonthSelection>(resolvedBase);

    const setBaseMonth = useCallback((m: MonthSelection) => {
        setBaseMonthState(m);
        setComparisonMonth(prev => {
            const stillAfter = prev.year > m.year || (prev.year === m.year && prev.monthIndex > m.monthIndex);
            if (stillAfter) {
                return prev;
            }
            return nextMonth(m);
        });
    }, []);
    const [thresholds, setThresholds] = useState<Thresholds>(() => loadThresholdsFromSession());
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [sortState, setSortState] = useState<SortState>(null);

    useEffect(() => {
        saveThresholdsToSession(thresholds);
    }, [thresholds]);

    useEffect(() => {
        saveDates(baseMonth, comparisonMonth);
    }, [baseMonth, comparisonMonth]);

    const setThreshold = useCallback((metric: MetricKey, kind: "increase" | "decrease", value: number | null) => {
        setThresholds(prev => ({
            ...prev,
            [metric]: {...prev[metric], [kind]: value},
        }));
    }, []);

    const allowedBaseMonths = useMemo(() => {
        if (!effectiveEarliestBase) {
            return [];
        }
        return buildMonthRange(effectiveEarliestBase, previousMonth(now));
    }, [effectiveEarliestBase, now]);

    const allowedComparisonMonths = useMemo(() => {
        if (allowedBaseMonths.length === 0) {
            return [];
        }
        const startingAtBase = buildMonthRange(baseMonth, now);
        return startingAtBase.slice(1);
    }, [allowedBaseMonths, baseMonth, now]);

    useEffect(() => {
        if (!effectiveEarliestBase) {
            return;
        }

        const newBase = isInRange(baseMonth, effectiveEarliestBase, previousMonth(now))
            ? baseMonth
            : effectiveEarliestBase;

        const comparisonLowerBound = nextMonth(newBase);
        const comparisonAvailable = monthCompare(comparisonLowerBound, now) <= 0;
        const comparisonValid = comparisonAvailable
            && isInRange(comparisonMonth, comparisonLowerBound, now);

        if (newBase !== baseMonth) {
            setBaseMonthState(newBase);
        }
        if (!comparisonValid && comparisonAvailable) {
            setComparisonMonth(comparisonLowerBound);
        }
    }, [baseMonth, comparisonMonth, effectiveEarliestBase, now]);

    return {
        availableProperties,
        selectedPropertyIds,
        setSelectedPropertyIds,
        baseMonth,
        setBaseMonth,
        comparisonMonth,
        setComparisonMonth,
        allowedBaseMonths,
        allowedComparisonMonths,
        thresholds,
        setThreshold,
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        sortState,
        setSortState,
        earliestReforecastMonth,
    };
}
